import { useMemo, useState, useEffect } from "react";
import { updatePatientProfile, fetchPatientProfile } from "../../services/api";
import "./assessment.css";

function scoreFagerstrom(a) {
  // a = answers object
  let score = 0;

  // 1) Time to first cigarette
  // "<5"=3, "6-30"=2, "31-60"=1, ">60"=0
  const ttf = a.firstCigAfter;
  if (ttf === "<5") score += 3;
  else if (ttf === "6-30") score += 2;
  else if (ttf === "31-60") score += 1;

  // 2) Difficult to refrain where smoking is forbidden (yes=1)
  if (a.difficultToRefrain === "yes") score += 1;

  // 3) Which cigarette would be hardest to give up (first morning=1)
  if (a.hardestCig === "firstMorning") score += 1;

  // 4) Cigarettes per day (<=10=0, 11–20=1, 21–30=2, >=31=3)
  const cigs = Number(a.cigsPerDay || 0);
  if (cigs >= 31) score += 3;
  else if (cigs >= 21) score += 2;
  else if (cigs >= 11) score += 1;

  // 5) Smoke more in the morning (yes=1)
  if (a.moreInMorning === "yes") score += 1;

  // 6) Smoke when ill in bed (yes=1)
  if (a.smokeWhenIll === "yes") score += 1;

  return score;
}

function levelFromScore(score) {
  if (score <= 2) return { label: "Very Low", color: "lvl-verylow" };
  if (score <= 4) return { label: "Low", color: "lvl-low" };
  if (score === 5) return { label: "Medium", color: "lvl-medium" };
  if (score <= 7) return { label: "High", color: "lvl-high" };
  return { label: "Very High", color: "lvl-veryhigh" };
}

export default function Assessment() {
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    // bio
    fullName: "",
    age: "",
    gender: "",
    phone: "",
    // smoking history
    yearsSmoking: "",
    cigsPerDay: "",
    previousAttempts: "",
    quitGoalDate: "",
    // FTND
    firstCigAfter: "",
    difficultToRefrain: "",
    hardestCig: "",
    moreInMorning: "",
    smokeWhenIll: "",
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setSaved(false);
  };

  const ftndReady = useMemo(() => {
    const req = [
      "firstCigAfter",
      "difficultToRefrain",
      "hardestCig",
      "cigsPerDay",
      "moreInMorning",
      "smokeWhenIll",
    ];
    return req.every((k) => String(form[k] || "").length > 0);
  }, [form]);

  const score = useMemo(() => (ftndReady ? scoreFagerstrom(form) : null), [ftndReady, form]);
  const level = useMemo(() => (score !== null ? levelFromScore(score) : null), [score]);

  useEffect(() => {
    fetchPatientProfile().then(prof => {
      setForm(f => ({
        ...f,
        fullName: `${prof.first_name || ''} ${prof.last_name || ''}`.trim(),
        phone: prof.phone || "",
        gender: prof.gender || "",
        cigsPerDay: prof.cigarettes_per_day || "",
        previousAttempts: prof.quit_attempts || "",
      }));
    }).catch(err => console.error("Could not fetch profile for assessment init:", err));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const [first_name, ...lastParts] = form.fullName.split(" ");
    const last_name = lastParts.join(" ");

    const payload = {
      first_name,
      last_name,
      phone: form.phone,
      gender: form.gender,
      cigarettes_per_day: parseInt(form.cigsPerDay) || 0,
      quit_attempts: parseInt(form.previousAttempts) || 0,
      years_smoking: parseInt(form.yearsSmoking) || 0,
      goals: `FTND Score: ${score}, Level: ${level?.label}`
    };

    try {
      await updatePatientProfile(payload);
      setSaved(true);
      alert("Assessment saved to your profile.");
    } catch (err) {
      console.error("Failed to save assessment:", err);
      alert("Error saving assessment. Please try again.");
    }
  };

  return (
    <section className="assess">
      <header className="assess-head">
        <h1>Initial Assessment</h1>
        <p className="muted">
          Tell us about you and your smoking pattern. We’ll estimate your nicotine dependence.
        </p>
      </header>

      <form className="assess-grid" onSubmit={submit}>
        {/* Bio */}
        <div className="card">
          <h3>About You</h3>
          <label>
            Full Name
            <input name="fullName" value={form.fullName} onChange={onChange} placeholder="e.g. Amina Haddad" />
          </label>
          <div className="row2">
            <label>
              Age
              <input type="number" name="age" value={form.age} onChange={onChange} placeholder="e.g. 29" />
            </label>
            <label>
              Gender (optional)
              <select name="gender" value={form.gender} onChange={onChange}>
                <option value="">Select…</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other / Prefer not to say</option>
              </select>
            </label>
          </div>
          <label>
            Phone (optional)
            <input name="phone" value={form.phone} onChange={onChange} placeholder="+216 …" />
          </label>
        </div>

        {/* Smoking History */}
        <div className="card">
          <h3>Smoking History</h3>
          <div className="row2">
            <label>
              Years smoking
              <input type="number" name="yearsSmoking" value={form.yearsSmoking} onChange={onChange} placeholder="e.g. 8" />
            </label>
            <label>
              Cigarettes per day
              <input type="number" name="cigsPerDay" value={form.cigsPerDay} onChange={onChange} placeholder="e.g. 12" />
            </label>
          </div>
          <label>
            Previous quit attempts (optional)
            <input name="previousAttempts" value={form.previousAttempts} onChange={onChange} placeholder="e.g. 2" />
          </label>
          <label>
            Target quit date (optional)
            <input type="date" name="quitGoalDate" value={form.quitGoalDate} onChange={onChange} />
          </label>
        </div>

        {/* Fagerström */}
        <div className="card">
          <h3>Nicotine Dependence</h3>

          <label>
            Time to first cigarette after waking
            <select name="firstCigAfter" value={form.firstCigAfter} onChange={onChange} required>
              <option value="">Select…</option>
              <option value="<5">Within 5 minutes</option>
              <option value="6-30">6–30 minutes</option>
              <option value="31-60">31–60 minutes</option>
              <option value=">60">After 60 minutes</option>
            </select>
          </label>

          <label>
            Difficult to refrain where smoking is forbidden?
            <select name="difficultToRefrain" value={form.difficultToRefrain} onChange={onChange} required>
              <option value="">Select…</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>

          <label>
            The cigarette you’d hate most to give up
            <select name="hardestCig" value={form.hardestCig} onChange={onChange} required>
              <option value="">Select…</option>
              <option value="firstMorning">The first one in the morning</option>
              <option value="others">Any other</option>
            </select>
          </label>

          <label>
            Do you smoke more during the first hours after waking?
            <select name="moreInMorning" value={form.moreInMorning} onChange={onChange} required>
              <option value="">Select…</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>

          <label>
            Do you smoke even when ill and confined to bed?
            <select name="smokeWhenIll" value={form.smokeWhenIll} onChange={onChange} required>
              <option value="">Select…</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>

          {/* Live score */}
          <div className="score">
            <div className="score-left">
              <div className="score-title">Estimated Dependence</div>
              {score === null ? (
                <div className="score-muted">Complete the answers to see your score.</div>
              ) : (
                <div className="score-big">
                  <span className={`badge ${level.color}`}>{level.label}</span>
                  <span className="score-val">{score} / 10</span>
                </div>
              )}
            </div>
            <ul className="score-legend">
              <li><span>0–2</span> Very Low</li>
              <li><span>3–4</span> Low</li>
              <li><span>5</span> Medium</li>
              <li><span>6–7</span> High</li>
              <li><span>8–10</span> Very High</li>
            </ul>
          </div>
        </div>

        <div className="actions">
          <button className="btn primary" type="submit">Save Assessment</button>
          {saved && <span className="saved">✓ Saved</span>}
        </div>

        <p className="disclaimer">
          This screening provides an estimate and is not a diagnosis. A healthcare professional should validate results.
        </p>
      </form>
    </section>
  );
}
